// ============================================================================
// secureRest — acesso REST pelo PROXY autenticado (em vez da chave pública)
// ============================================================================
// ANTES os módulos batiam direto em https://...supabase.co/rest/v1/<tabela>
// com a chave PÚBLICA (anon) → qualquer um lia/editava. Agora passam por
// /api/db/rest/v1/<tabela> com x-session-token, e o servidor (chave de serviço)
// fala com o Supabase. A sintaxe PostgREST continua IGUAL — só muda a base e
// os headers (sem apikey/Authorization pública).
//
// Uso (substitui as constantes locais SUPABASE_URL/headers dos módulos):
//   import { SUPABASE_URL, restHeaders } from './secureRest.js';
//   fetch(`${SUPABASE_URL}/rest/v1/clients?...`, { headers: restHeaders() })
//   fetch(url, { method:'POST', headers: restHeaders({ Prefer:'return=representation' }), body })
// ============================================================================

export function sessionToken() {
  try {
    return JSON.parse(sessionStorage.getItem('fk_session') || '{}')?.sessionToken || '';
  } catch {
    return '';
  }
}

// Base do proxy no nosso próprio servidor. As URLs viram /api/db/rest/v1/<tabela>.
export const SUPABASE_URL = '/api/db';

// Headers com o token de sessão (lido na hora da chamada, não no import).
export function restHeaders(extra = {}) {
  return {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken(),
    ...extra,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// REGRA LINEAR DE LEITURA (incidente 15/07/2026 — "comandas sumiram")
// ═══════════════════════════════════════════════════════════════════════════
// O padrão antigo `if (!r.ok) return []` fazia QUALQUER falha de rede/servidor
// virar LISTA VAZIA — a tela mostrava "não há dados" com o banco intacto, e o
// usuário entendia como dado apagado. A partir de agora TODA leitura segue
// UMA regra: falhou → LANÇA erro (quem chama mantém o estado anterior e
// avisa); truncou → PAGINA até o fim (o PostgREST corta em 1000 linhas em
// silêncio — a janela de comandas tinha 4.704 e o app só recebia 1.000).

/** fetch que só aceita JSON válido. Falha (status, HTML, parse) → throw. */
export async function restFetchJson(url, opts = {}) {
  const { headers: extraHeaders, ...rest } = opts;
  const r = await fetch(url, { headers: restHeaders(extraHeaders || {}), ...rest });
  const text = await r.text();
  if (!r.ok) {
    throw new Error(`HTTP ${r.status} em ${url.split('?')[0]}: ${text.slice(0, 140)}`);
  }
  const trimmed = text.trim();
  if (trimmed.startsWith('<')) {
    // Fallback/erro HTML servido no lugar da API (proxy, plataforma, SW)
    throw new Error(`Resposta HTML inesperada em ${url.split('?')[0]} (servidor instável?)`);
  }
  try {
    return JSON.parse(trimmed || 'null');
  } catch {
    throw new Error(`JSON inválido em ${url.split('?')[0]}`);
  }
}

/**
 * GET paginado: busca TODAS as linhas de uma listagem PostgREST, driblando o
 * teto silencioso de 1000/request. `baseUrl` NÃO deve conter limit/offset.
 * Qualquer página que falhe → throw (nunca resultado parcial silencioso).
 */
export async function restFetchAll(baseUrl, { pageSize = 1000, maxRows = 50000 } = {}) {
  const sep = baseUrl.includes('?') ? '&' : '?';
  const first = await fetch(`${baseUrl}${sep}limit=${pageSize}`, {
    headers: restHeaders({ Prefer: 'count=exact' }),
  });
  const firstText = await first.text();
  if (!first.ok) throw new Error(`HTTP ${first.status} em ${baseUrl.split('?')[0]}: ${firstText.slice(0, 140)}`);
  if (firstText.trim().startsWith('<')) throw new Error(`Resposta HTML inesperada em ${baseUrl.split('?')[0]}`);
  const firstRows = JSON.parse(firstText || '[]');
  const range = first.headers.get('content-range') || '';
  const totalMatch = range.match(/\/(\d+)$/);
  const total = Math.min(totalMatch ? Number(totalMatch[1]) : firstRows.length, maxRows);
  if (firstRows.length >= total) return firstRows;

  const pages = [];
  for (let offset = firstRows.length; offset < total; offset += pageSize) {
    pages.push(restFetchJson(`${baseUrl}${sep}limit=${pageSize}&offset=${offset}`));
  }
  const rest2 = await Promise.all(pages); // qualquer página falha → throw
  return [...firstRows, ...rest2.flat()];
}
